import Papa from 'papaparse';

/**
 * 加载CSV文件数据
 * @param {string} fileName - CSV文件名
 * @returns {Promise<Array>} - 解析后的数据数组
 */
export const loadCSVData = async (fileName) => {
  try {
    const response = await fetch(`/data/${fileName}`);
    if (!response.ok) {
      throw new Error(`文件 ${fileName} 不存在或无法访问`);
    }
    
    const csvText = await response.text();
    return parseCSVData(csvText);
  } catch (error) {
    console.error('加载CSV文件失败:', error);
    throw error;
  }
};

/**
 * 解析CSV数据
 * @param {string} csvText - CSV文本内容
 * @returns {Array} - 解析后的数据数组
 */
export const parseCSVData = (csvText) => {
  const result = Papa.parse(csvText, {
    header: false,
    skipEmptyLines: true,
    transform: (value) => value.trim()
  });

  if (result.errors.length > 0) {
    console.warn('CSV解析警告:', result.errors);
  }

  // 转换为标准格式 [{time: '', value: ''}]
  const data = result.data.map((row, index) => {
    if (row.length >= 2) {
      return {
        time: row[0] || '',
        value: row[1] || ''
      };
    }
    return null;
  }).filter(item => item !== null && item.time !== '' && item.value !== '');

  return data;
};

/**
 * 从文件名解析站点、时间尺度、模型信息
 * @param {string} fileName - 文件名，格式如：高道_月_LSTM.csv
 * @returns {Object} - {site, timeScale, model}
 */
export const parseFileName = (fileName) => {
  // 移除文件扩展名
  const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
  
  // 按下划线分割
  const parts = nameWithoutExt.split('_');
  
  if (parts.length >= 3) {
    return {
      site: parts[0] || '',
      timeScale: parts[1] || '',
      model: parts[2] || ''
    };
  }
  
  return {
    site: '',
    timeScale: '',
    model: ''
  };
};

/**
 * 格式化时间显示
 * @param {string} time - 时间字符串
 * @param {string} timeScale - 时间尺度 ('年', '月', '天')
 * @returns {string} - 格式化后的时间字符串
 */
export const formatTime = (time, timeScale) => {
  if (!time) return '';
  
  switch (timeScale) {
    case '年':
      return `${time}年`;
    case '月':
      if (time.length === 6) {
        const year = time.substring(0, 4);
        const month = time.substring(4, 6);
        return `${year}年${month}月`;
      }
      return time;
    case '天':
      // 支持 2025/1/1 或 2025-01-01 格式
      if (time.includes('/')) {
        return time;
      } else if (time.includes('-')) {
        return time;
      }
      return time;
    default:
      return time;
  }
};

/**
 * 验证数据有效性
 * @param {Array} data - 数据数组
 * @returns {Object} - 验证结果
 */
export const validateData = (data) => {
  if (!Array.isArray(data)) {
    return { valid: false, message: '数据格式无效' };
  }
  
  if (data.length === 0) {
    return { valid: false, message: '数据为空' };
  }
  
  const invalidItems = data.filter(item => 
    !item.time || 
    item.value === null || 
    item.value === undefined || 
    item.value === ''
  );
  
  if (invalidItems.length === data.length) {
    return { valid: false, message: '所有数据都无效' };
  }
  
  return { 
    valid: true, 
    message: `共 ${data.length} 条数据，其中 ${data.length - invalidItems.length} 条有效` 
  };
};

/**
 * 导出数据为CSV格式
 * @param {Array} data - 要导出的数据
 * @param {string} fileName - 文件名（不含扩展名）
 */
export const exportData = (data, fileName = 'exported_data') => {
  if (!data || data.length === 0) {
    console.warn('没有数据可以导出');
    return;
  }

  // 准备CSV数据
  const csvData = [
    ['时间', '预测值'], // 表头
    ...data.map(item => [item.time, item.value])
  ];

  // 使用Papa Parse转换为CSV
  const csv = Papa.unparse(csvData);
  
  // 创建并下载文件
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

/**
 * 计算数据统计信息
 * @param {Array} data - 数据数组
 * @returns {Object} - 统计信息
 */
export const calculateStats = (data) => {
  if (!data || data.length === 0) {
    return { count: 0, max: 0, min: 0, avg: 0, sum: 0 };
  }

  const values = data
    .map(item => parseFloat(item.value))
    .filter(val => !isNaN(val));

  if (values.length === 0) {
    return { count: 0, max: 0, min: 0, avg: 0, sum: 0 };
  }

  const sum = values.reduce((acc, val) => acc + val, 0);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const avg = sum / values.length;

  return {
    count: values.length,
    max: max,
    min: min,
    avg: avg,
    sum: sum
  };
};